import { TerrainQualitySettings } from '@/planet/PlanetSurface';
import { DeviceCapabilities } from './Capabilities';

export type QualityLevel = 'low' | 'medium' | 'high' | 'ultra' | 'custom';

export interface QualitySettings {
  level: QualityLevel;
  /** Device pixel ratio cap. */
  pixelRatio: number;
  terrain: TerrainQualitySettings;
  /** Shadow map resolution; 0 disables shadows. */
  shadowMapSize: number;
  /** Shadow coverage distance, metres. */
  shadowDistance: number;
  /** Number of cascaded shadow splits. */
  shadowCascades: number;
  /** Ray-march steps for the atmosphere. 0 uses the cheap analytic fallback. */
  atmosphereSteps: number;
  /** Ray-march steps along the light direction inside the atmosphere. */
  atmosphereLightSteps: number;
  /** Cloud layer quality, 0 disables. */
  cloudQuality: number;
  /** Ocean detail level, 0 disables waves. */
  oceanQuality: number;
  /** Scatter (rock/vegetation) instances budget. 0 disables vegetation outright. */
  scatterBudget: number;
  /**
   * Side of one vegetation scatter cell, metres — the density knob.
   *
   * Density is set here rather than by a per-chunk instance cap because the
   * scatter grid belongs to the planet, not to a chunk: this value is
   * identical at every LOD level, so changing it thins or thickens the whole
   * forest without moving a single plant. See `VegetationScatter.ts`.
   *
   * A target, not an exact size. The grid has to be a power-of-two subdivision
   * of a cube face, so the achievable cell sizes on a given body are a factor
   * of two apart and the tiers here are spaced to land on distinct ones — 9 /
   * 5 / 2.5 m ask for 2^20 / 2^21 / 2^22 cells per face edge on an
   * Earth-sized world. Two tiers only a few tenths apart would quantise onto
   * the same grid and be the same setting.
   */
  vegetationCellMetres: number;
  /**
   * Multiplier on every biome's authored plants-per-km².
   *
   * 1 means "as dense as the biome definition says", which is the intended
   * look rather than a ceiling to push past — so the top tiers sit at 1 and
   * buy their extra fidelity from a finer cell grid and a longer
   * `scatterDistance` instead. Lower tiers thin the forest out; because the
   * presence roll is a fixed per-cell value, thinning removes plants without
   * relocating the survivors, so the same world just has fewer trees in it.
   */
  vegetationDensityScale: number;
  /** Distance at which surface scatter stops being drawn, metres. */
  scatterDistance: number;
  /** Enable the bloom pass. */
  bloom: boolean;
  /** Enable screen-space ambient occlusion. */
  ssao: boolean;
  /** Enable screen-space reflections on the ocean surface. */
  ssr: boolean;
  /** Resolution scale of the SSR scene capture, relative to the render size. */
  ssrScale: number;
  /** Number of stars in the background field. */
  starCount: number;
  /** Anisotropic filtering level. */
  anisotropy: number;
  /** Enable soft particle and volumetric effects. */
  volumetrics: boolean;
  /**
   * Preserve the drawing buffer so screenshots taken outside the render loop
   * capture the last frame. Costs a per-frame copy; used only by automated
   * visual checks.
   */
  captureMode?: boolean;
}

/**
 * Terrain streaming budgets.
 *
 * `splitFactor` is an *angular* criterion in disguise: a node splits when the
 * camera is within `splitFactor` chunk-widths of it, so at the threshold the
 * chunk subtends about `1 / splitFactor` radians and one of its quads subtends
 * `1 / (resolution * splitFactor)`. That product is the only thing that decides
 * how coarse the terrain looks, and it has to stay near a hundred for a quad to
 * land around eight pixels.
 *
 * Pushing the split factor below about 1.5 to save draw calls does not make the
 * terrain slightly blockier, it makes it stop subdividing altogether at long
 * range: a root chunk spans some fourteen degrees seen from four planetary
 * radii, and a factor of 0.9 does not ask for a split until a chunk spans
 * fifty. The planet then renders as two or three enormous chunks, which reads
 * as a smooth shaded bubble with the atmosphere on top and no surface at all.
 *
 * Within that constraint, fidelity is set by the *product* while the draw call
 * count goes as the square of the split factor alone. So for a given quad size
 * the cheaper configuration is the larger chunk — high resolution with the
 * smallest split factor that still subdivides at range — which is why the
 * resolutions here are 48 and 64 rather than 16 or 32.
 *
 * **`maxChunks` has to hold the whole live quadtree, not just the leaves that
 * get drawn.** Reaching metre-scale detail on a six-thousand-kilometre body
 * takes on the order of sixteen levels, and every one of them keeps a ring of
 * siblings resident. Sizing the budget for the visible set alone forces the
 * streamer to evict nodes the tree is still using, and the terrain then
 * collapses and rebuilds on a loop.
 */
const BASE_TERRAIN: TerrainQualitySettings = {
  resolution: 64,
  splitFactor: 1.9,
  mergeFactor: 2.3,
  maxChunks: 1200,
  maxPendingBuilds: 64,
  targetVertexSpacing: 3,
};

export const QUALITY_PROFILES: Record<Exclude<QualityLevel, 'custom'>, QualitySettings> = {
  low: {
    level: 'low',
    pixelRatio: 1,
    terrain: { ...BASE_TERRAIN, resolution: 48, splitFactor: 1.5, mergeFactor: 1.85, maxChunks: 900, maxPendingBuilds: 32, targetVertexSpacing: 8 },
    shadowMapSize: 0,
    shadowDistance: 300,
    shadowCascades: 1,
    atmosphereSteps: 0,
    atmosphereLightSteps: 0,
    cloudQuality: 0,
    oceanQuality: 0,
    scatterBudget: 0,
    vegetationCellMetres: 0,
    vegetationDensityScale: 0,
    scatterDistance: 0,
    bloom: false,
    ssao: false,
    ssr: false,
    ssrScale: 0,
    starCount: 2600,
    anisotropy: 1,
    volumetrics: false,
  },
  medium: {
    level: 'medium',
    pixelRatio: 1,
    terrain: { ...BASE_TERRAIN, resolution: 48, splitFactor: 1.9, mergeFactor: 2.3, maxChunks: 1100, maxPendingBuilds: 48, targetVertexSpacing: 5 },
    shadowMapSize: 1024,
    shadowDistance: 900,
    shadowCascades: 2,
    atmosphereSteps: 8,
    atmosphereLightSteps: 3,
    cloudQuality: 1,
    oceanQuality: 1,
    scatterBudget: 6000,
    vegetationCellMetres: 9,
    vegetationDensityScale: 0.4,
    scatterDistance: 600,
    bloom: true,
    ssao: false,
    ssr: false,
    ssrScale: 0,
    starCount: 7000,
    anisotropy: 4,
    volumetrics: false,
  },
  high: {
    level: 'high',
    pixelRatio: 1.5,
    terrain: { ...BASE_TERRAIN },
    shadowMapSize: 2048,
    shadowDistance: 2200,
    shadowCascades: 3,
    atmosphereSteps: 16,
    atmosphereLightSteps: 5,
    cloudQuality: 2,
    oceanQuality: 2,
    scatterBudget: 20000,
    vegetationCellMetres: 5,
    vegetationDensityScale: 1,
    scatterDistance: 1400,
    bloom: true,
    ssao: true,
    ssr: true,
    ssrScale: 0.6,
    starCount: 14000,
    anisotropy: 8,
    volumetrics: true,
  },
  ultra: {
    level: 'ultra',
    pixelRatio: 2,
    terrain: { ...BASE_TERRAIN, resolution: 64, splitFactor: 2.3, mergeFactor: 2.8, maxChunks: 1500, maxPendingBuilds: 96, targetVertexSpacing: 2 },
    shadowMapSize: 4096,
    shadowDistance: 4000,
    shadowCascades: 4,
    atmosphereSteps: 32,
    atmosphereLightSteps: 8,
    cloudQuality: 3,
    oceanQuality: 3,
    scatterBudget: 48000,
    vegetationCellMetres: 2.5,
    vegetationDensityScale: 1,
    scatterDistance: 2600,
    bloom: true,
    ssao: true,
    ssr: true,
    ssrScale: 0.85,
    starCount: 24000,
    anisotropy: 16,
    volumetrics: true,
  },
};

/**
 * Picks a starting profile from detected capabilities.
 *
 * The game must remain *mechanically* complete at every level: `low` disables
 * shadows, atmospheric scattering, clouds, ocean waves and surface scatter, but
 * every simulation system still runs and every piece of information the player
 * needs is still on screen. Visual quality is the only thing that scales.
 */
export function selectQualityProfile(caps: DeviceCapabilities): QualitySettings {
  if (!caps.webgl2 || caps.softwareRenderer) {
    return { ...QUALITY_PROFILES.low, pixelRatio: 1 };
  }

  const cores = caps.hardwareConcurrency;
  const memory = caps.deviceMemory ?? 4;

  let level: Exclude<QualityLevel, 'custom'> = 'medium';
  if (cores >= 12 && memory >= 8 && caps.maxTextureSize >= 8192) level = 'ultra';
  else if (cores >= 8 && memory >= 6) level = 'high';
  else if (cores <= 4 || memory <= 2) level = 'low';

  const profile: QualitySettings = { ...QUALITY_PROFILES[level] };
  profile.pixelRatio = Math.min(profile.pixelRatio, caps.devicePixelRatio);
  profile.anisotropy = Math.min(profile.anisotropy, caps.anisotropy);

  if (caps.reducedMotion) {
    // Respecting the preference means cutting the things that move for their own
    // sake: volumetrics, wave animation and bloom flicker.
    profile.volumetrics = false;
    profile.bloom = false;
    profile.oceanQuality = Math.min(profile.oceanQuality, 1);
  }

  return profile;
}
